import { useEffect, useRef, useState } from "react";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

const STAGES = {
  card: ["Aguarde...", "Validando cartão...", "Finalizando..."],
  pix: ["Aguarde...", "Gerando PIX...", "Finalizando..."],
};

/**
 * Barra de progresso animada (verde) com etapas durante o processamento do pagamento.
 * - active: enche a barra gradualmente até ~92% e alterna as mensagens de etapa.
 * - error: barra vermelha + mensagem do erro.
 * - success: barra verde cheia + mensagem de sucesso.
 */
export const PaymentProgress = ({ active, method = "card", error = "", success = false }) => {
  const stages = STAGES[method] || STAGES.card;
  const [pct, setPct] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    timers.current.forEach((t) => clearInterval(t) || clearTimeout(t));
    timers.current = [];
    if (error || success) {
      setPct(100);
      return;
    }
    if (!active) {
      setPct(0);
      setStageIdx(0);
      return;
    }
    setPct(8);
    setStageIdx(0);
    const fill = setInterval(() => {
      setPct((p) => (p >= 92 ? 92 : p + Math.max(1.5, (92 - p) * 0.07)));
    }, 130);
    timers.current.push(fill);
    timers.current.push(setTimeout(() => setStageIdx(1), 1100));
    timers.current.push(setTimeout(() => setStageIdx(2), 2600));
    return () => {
      timers.current.forEach((t) => clearInterval(t) || clearTimeout(t));
      timers.current = [];
    };
  }, [active, error, success, method]);

  if (!active && !error && !success) return null;

  const barColor = error ? "bg-red-500" : "bg-emerald-500";
  const textColor = error ? "text-red-700" : success ? "text-emerald-700" : "text-slate-600";
  const label = error
    ? error
    : success
    ? (typeof success === "string" ? success : "Pagamento aprovado!")
    : stages[stageIdx];

  return (
    <div className="space-y-2 py-1" data-testid="payment-progress">
      <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-300 ease-out`}
          style={{ width: `${pct}%` }}
          data-testid="payment-progress-bar"
        />
      </div>
      <div className={`flex items-center gap-2 text-sm font-semibold ${textColor}`} data-testid="payment-progress-label">
        {error ? (
          <XCircle size={16} weight="fill" />
        ) : success ? (
          <CheckCircle size={16} weight="fill" />
        ) : (
          <span className="inline-block w-3.5 h-3.5 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin" />
        )}
        <span>{label}</span>
      </div>
    </div>
  );
};

export default PaymentProgress;
